import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ClientPicker from "@/components/client/ClientPicker";
import { useAuth } from "@/context/AuthContext";
import DateInput from "@/components/ui/date-input";

export const PRO_TYPES = [
  "Trade License", "Tenancy Contract", "Establishment Card", "Employment Visa",
  "Partner Visa", "Golden Visa", "Labour Card", "Emirates ID",
  "Medical Insurance", "ILOE", "Liquidation (45 Days)", "WPS (Monthly)",
];
export const PRO_STATUSES = ["Not Started", "In Progress", "Completed", "Cancelled"];

const empty = {
  compliance_type: "Trade License", client_name: "", client_id: "",
  responsible_person_id: "", professional_fees: "",
  expiry_date: "", reminder_days: 30, status: "Not Started", remark: "",
  action_2: "Invoice Pending",
};

const ACTION2_OPTIONS = ["Invoice Pending", "Invoiced", "Partly Paid", "Paid"];

export default function ProComplianceForm({ open, onClose, initial, users, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          compliance_type: initial.compliance_type,
          client_name: initial.client_name,
          client_id: initial.client_id || "",
          responsible_person_id: initial.responsible_person_id,
          professional_fees: initial.professional_fees ?? "",
          expiry_date: (initial.expiry_date || "").slice(0, 10),
          reminder_days: initial.reminder_days ?? 30,
          status: initial.status || "Not Started",
          remark: initial.remark || "",
          action_2: initial.action_2 || "Invoice Pending",
        });
      } else {
        setForm(empty);
      }
    }
  }, [open, initial]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const allowedTypes = (user?.allowed_pro_types && user.allowed_pro_types.length) ? user.allowed_pro_types : PRO_TYPES;
  const typeOptions = useMemo(() => PRO_TYPES.filter((t) => allowedTypes.includes(t)), [allowedTypes]);
  const nonAdminUsers = useMemo(() => users.filter((u) => u.role !== "administrator"), [users]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.responsible_person_id || !form.expiry_date) {
      toast.error("Client, responsible person and expiry date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, professional_fees: Number(form.professional_fees) || 0 };
      if (initial) {
        await api.patch(`/pro-compliances/${initial.id}`, payload);
        toast.success("PRO compliance updated");
      } else {
        await api.post("/pro-compliances", payload);
        toast.success("PRO compliance created");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{initial ? "Edit PRO Compliance" : "New PRO Compliance"}</DialogTitle>
          <DialogDescription>Government PRO services with expiry date and reminder days.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="pro-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Type</Label>
              <Select value={form.compliance_type} onValueChange={(v) => set({ compliance_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="pro-type"><SelectValue /></SelectTrigger>
                <SelectContent>{typeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Client</Label>
              <div className="mt-1.5">
                <ClientPicker
                  value={form.client_id ? { id: form.client_id, name: form.client_name } : null}
                  onChange={(c) => set({ client_id: c?.id || "", client_name: c?.name || "" })}
                  testid="pro-client-picker"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Responsible Person</Label>
              <Select value={form.responsible_person_id} onValueChange={(v) => set({ responsible_person_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="pro-responsible"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {nonAdminUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Professional Fees (AED)</Label>
              <Input type="number" min="0" step="1" value={form.professional_fees}
                onChange={(e) => set({ professional_fees: e.target.value })}
                placeholder="0"
                className="mt-1.5 font-mono-num" data-testid="pro-fees" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-600">Expiry Date</Label>
              <div className="mt-1.5">
                <DateInput value={form.expiry_date} onChange={(v) => set({ expiry_date: v })} testid="pro-expiry" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Reminder (days)</Label>
              <Input type="number" min="0" step="1" value={form.reminder_days}
                onChange={(e) => set({ reminder_days: Number(e.target.value) })}
                className="mt-1.5 font-mono-num" data-testid="pro-reminder-days" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Status</Label>
            <Select value={form.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger className="mt-1.5" data-testid="pro-status"><SelectValue /></SelectTrigger>
              <SelectContent>{PRO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Invoice Status</Label>
            <Select value={form.action_2} onValueChange={(v) => set({ action_2: v })}>
              <SelectTrigger className="mt-1.5" data-testid="pro-action-2"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTION2_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Remark</Label>
            <Textarea value={form.remark} onChange={(e) => set({ remark: e.target.value })} rows={2} className="mt-1.5" data-testid="pro-remark" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="pro-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {initial ? "Save changes" : "Create PRO compliance"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
